import { supabase } from "@/integrations/supabase/client";
import { pickAccessStore, storesRowGrantsPaid, subscriptionsForStore } from "@/lib/accessStore";
import { subscriptionFallbackIds } from "@/lib/subscriptionLookup";

export type AccessLevel = "paid" | "trial" | "blocked";

export interface AccessResult {
  level: AccessLevel;
  daysLeft?: number;
  hasPaidAccess: boolean;
}

/**
 * A store subscription restores dashboard access when it is 'active' and not past
 * its end date — even after the free trial has expired. store_subscriptions rows are
 * written only by the server (no client write policy).
 */
export function hasActiveStoreSubscription(
  rows: { status: string | null; end_date: string | null }[] | null | undefined,
  now: Date = new Date(),
): boolean {
  return (rows ?? []).some(
    (r) => r.status === "active" && (!r.end_date || new Date(r.end_date).getTime() > now.getTime()),
  );
}

/**
 * Fallback ids for the access check: the merchant's OWN store_settings ids and OWN stores
 * ids (owner_id = userId — stores matched only by e-mail are excluded), minus the real
 * store id, never the auto-created "my-store" row.
 */
export function accessSubscriptionFallbackIds(
  userId: string,
  realStoreId: string,
  settingsRows: { id: string; created_at?: string | null }[] | null | undefined,
  storesRows: { id: string; created_at?: string | null; owner_id?: string | null; store_name?: string | null }[] | null | undefined,
): string[] {
  const ownStores = (storesRows ?? []).filter((r) => r.owner_id === userId);
  return subscriptionFallbackIds(realStoreId, settingsRows, ownStores, { excludeAutoStore: true });
}

/**
 * A fallback subscription grants access only when it is on one of the fallback ids, is
 * active and not past its end date, AND is a paid plan (a "free" plan never grants paid access).
 */
export function fallbackSubscriptionGrantsPaid(
  rows: { store_id: string | null; status: string | null; end_date: string | null; plans?: { slug?: string | null } | null }[] | null | undefined,
  fallbackIds: string[],
  now: Date = new Date(),
): boolean {
  const eligible = (rows ?? []).filter(
    (r) => !!r.store_id && fallbackIds.includes(r.store_id) && !!r.plans?.slug && r.plans.slug !== "free",
  );
  return hasActiveStoreSubscription(eligible, now);
}

/**
 * Store-first access control.
 * Single source of truth: the `stores` table.
 *
 * IF store.plan != 'free' OR store.billing_status == 'active' → PAID
 * ELSE → BLOCKED
 */
export async function checkUserAccess(userId: string, userEmail?: string | null): Promise<AccessResult> {
  console.log("SUPABASE URL:", (supabase as any).supabaseUrl);
  console.log("[AccessControl] Checking access for user:", userId, "email:", userEmail ?? "(none)");

  const email = userEmail?.trim().toLowerCase();

  // Build OR filter: owner_id or owner_email
  const orParts: string[] = [`owner_id.eq.${userId}`];
  if (email) {
    orParts.push(`owner_email.eq.${email}`);
  }

  // The merchant's REAL store is the only store access may be decided on
  // (never the auto-created "my-store" stores row when a store_settings row exists).
  let access: ReturnType<typeof pickAccessStore> = null;
  let ownSettingsRows: any[] = [];
  let ownStoresRows: any[] = [];
  try {
    const [{ data: settingsRows }, { data: storesRows, error }] = await Promise.all([
      supabase.from("store_settings").select("id, created_at").eq("user_id", userId),
      (supabase.from("stores") as any).select("*").or(orParts.join(",")),
    ]);
    if (error) console.log("STORE QUERY ERROR:", error);
    ownSettingsRows = (settingsRows as any[]) ?? [];
    ownStoresRows = (storesRows as any[]) ?? [];
    access = pickAccessStore(settingsRows as any[], storesRows as any[]);
    console.log("[AccessControl] Real store:", access?.storeId ?? "(none)");

    if (storesRowGrantsPaid(access?.storesRow)) {
      console.log("[AccessControl] → PAID (real store stores row)");
      return { level: "paid", hasPaidAccess: true };
    }
  } catch (err) {
    console.error("[AccessControl] Store lookup exception:", err);
  }

  // Fallback: store_settings payment confirmed server-side only.
  // payment_status / stripe_payment_intent_id are write-protected by a DB trigger
  // (only the service role — webhook / verify-and-generate-store — can set them),
  // and both must be present: a Stripe PaymentIntent proves the payment was verified.
  try {
    const { data, error } = await supabase
      .from("store_settings")
      .select("id, payment_status, stripe_payment_intent_id, store_type, trial_end_date")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!error && data && data.payment_status === "completed" && !!(data as any).stripe_payment_intent_id) {
      console.log("[AccessControl] → PAID (store_settings: server-verified Stripe payment)");
      return { level: "paid", hasPaidAccess: true };
    }

    // Manual creation: free 14-day trial (no card) grants dashboard access
    if (!error && data && data.store_type === "manual" && data.trial_end_date) {
      const msLeft = new Date(data.trial_end_date).getTime() - Date.now();
      if (msLeft > 0) {
        return { level: "trial", hasPaidAccess: false, daysLeft: Math.ceil(msLeft / 86400000) };
      }
    }
  } catch {
    // continue
  }

  // Active subscription of the REAL store — a "my-store" subscription never grants access.
  try {
    if (access) {
      const { data: subs } = await supabase
        .from("store_subscriptions")
        .select("store_id, status, end_date")
        .eq("store_id", access.storeId)
        .eq("status", "active");
      if (hasActiveStoreSubscription(subscriptionsForStore(subs as any[], access.storeId))) {
        console.log("[AccessControl] → PAID (active subscription of the real store)");
        return { level: "paid", hasPaidAccess: true };
      }

      // Double key (read alignment): a subscription written with another of the merchant's
      // OWN ids (e.g. their stores.id). Own rows only, never the auto-created "my-store" row.
      const fallbackIds = accessSubscriptionFallbackIds(userId, access.storeId, ownSettingsRows, ownStoresRows);
      if (fallbackIds.length > 0) {
        const { data: fbSubs } = await supabase
          .from("store_subscriptions")
          .select("store_id, status, end_date, plans(slug)")
          .in("store_id", fallbackIds)
          .eq("status", "active");
        if (fallbackSubscriptionGrantsPaid(fbSubs as any[], fallbackIds)) {
          console.log("[AccessControl] → PAID (active paid subscription on another own store id)");
          return { level: "paid", hasPaidAccess: true };
        }
      }
    }
  } catch {
    // continue
  }

  console.log("[AccessControl] → BLOCKED");
  return { level: "blocked", hasPaidAccess: false };
}
